import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const CoinTossPage: React.FC = () => {
  const navigate = useNavigate();
  const coinRef = useRef<HTMLDivElement>(null);

  const [phase, setPhase] = useState<'ready' | 'flipping' | 'revealed'>('ready');
  const [flipResult, setFlipResult] = useState<'heads' | 'tails'>('heads');
  const goBack = useCallback(() => navigate(-1), [navigate]);

  const handleFlip = useCallback(() => {
    setPhase('flipping');
    const result: 'heads' | 'tails' = Math.random() < 0.5 ? 'heads' : 'tails';
    setFlipResult(result);
    // Cancel previous animation before starting a new one
    coinRef.current?.getAnimations().forEach(a => a.cancel());
    // Use Web Animations API for reliable per-flip animation
    const finalDeg = result === 'heads' ? 3240 : 3420;
    coinRef.current?.animate(
      [
        { transform: 'rotateY(0deg) scale(1)' },
        { transform: 'rotateY(900deg) scale(1.2)', offset: 0.25 },
        { transform: 'rotateY(1800deg) scale(1.1)', offset: 0.5 },
        { transform: 'rotateY(2520deg) scale(1.05)', offset: 0.75 },
        { transform: `rotateY(${finalDeg}deg) scale(1)` },
      ],
      { duration: 2000, easing: 'ease-out', fill: 'forwards' }
    );

    setTimeout(() => setPhase('revealed'), 2000);
  }, []);

  const handleReset = useCallback(() => {
    // Cancel current animation, reset coin to initial position, then immediately flip again
    coinRef.current?.getAnimations().forEach(a => a.cancel());
    // Use rAF to ensure the cancel takes effect before starting the new animation
    requestAnimationFrame(() => handleFlip());
  }, [handleFlip]);

  return (
    <div className="fixed inset-0 z-50 bg-stone-900 flex flex-col items-center justify-center">
      <style>{`
        .coin-container {
          perspective: 1000px;
        }
        .coin {
          width: 10rem;
          height: 10rem;
          position: relative;
          transform-style: preserve-3d;
        }
        .coin-face {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          backface-visibility: hidden;
          border: 4px solid rgba(255,255,255,0.3);
        }
        .coin-heads {
          background: linear-gradient(135deg, #F59E0B, #D97706);
          box-shadow: 0 0 30px rgba(245, 158, 11, 0.4), inset 0 2px 4px rgba(255,255,255,0.3);
        }
        .coin-tails {
          background: linear-gradient(135deg, #EA580C, #9A3412);
          box-shadow: 0 0 30px rgba(234, 88, 12, 0.4), inset 0 2px 4px rgba(255,255,255,0.3);
          transform: rotateY(180deg);
        }
      `}</style>

      {/* Close Button */}
      <button
        onClick={goBack}
        className="absolute top-4 right-4 z-20 p-2 bg-stone-100/10 hover:bg-primary-500/20 rounded-full text-white transition-colors"
      >
        <XMarkIcon className="h-8 w-8" />
      </button>

      {/* Coin + Controls */}
      <div className="flex flex-col items-center gap-8">
        <h1 className="text-white text-fluid-h1 font-bold font-serif">Coin Toss</h1>

        <div className="coin-container">
          <div className="coin" ref={coinRef}>
            <div className="coin-face coin-heads">
              <div className="flex flex-col items-center gap-1">
                <img src="/images/meeple.svg" alt="heads" className="w-12 h-12 opacity-90" style={{ filter: 'brightness(0) invert(1)' }} />
                <span className="text-white/80 text-xs font-bold uppercase tracking-wider">Heads</span>
              </div>
            </div>
            <div className="coin-face coin-tails">
              <div className="flex flex-col items-center gap-1">
                <img src="/images/trophy.svg" alt="tails" className="w-12 h-12 opacity-90" style={{ filter: 'brightness(0) invert(1)' }} />
                <span className="text-white/80 text-xs font-bold uppercase tracking-wider">Tails</span>
              </div>
            </div>
          </div>
        </div>

        {/* Result text */}
        {phase === 'revealed' && (
          <div className="bg-amber-50 px-8 py-4 rounded-2xl shadow-xl border border-stone-200">
            <span className="text-4xl font-black font-serif text-stone-900">
              {flipResult === 'heads' ? 'Heads' : 'Tails'}!
            </span>
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-4">
          {phase === 'revealed' && (
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-5 py-3 bg-stone-600 hover:bg-stone-700 text-white rounded-2xl font-bold text-base shadow-2xl transition hover:scale-105 active:scale-95"
            >
              <ArrowPathIcon className="h-5 w-5" />
              Flip Again
            </button>
          )}
          {phase !== 'revealed' && (
            <button
              onClick={handleFlip}
              disabled={phase === 'flipping'}
              className="px-8 py-4 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white rounded-2xl font-bold text-xl shadow-2xl transition hover:scale-105 active:scale-95 disabled:scale-100"
            >
              {phase === 'flipping' ? 'Flipping...' : 'Flip!'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoinTossPage;
